<template>
  <div class="component_container h-100" ref="componentContainer">
    <!-- Table -->
    <div v-if="showLoading" class="component_flexible h-100">
      <SkeletonSingleCommodity :showFields="showFields" />
    </div>
    <div v-else class="component_flexible flex-1 flex flex-col">
      <DataTable
        columnResizeMode="expand"
        class="p-datatable-sm overview_table table-bordered-column"
        resizableColumns
        scrollable
        scrollHeight="flex"
        :key="showFields ? 'full' : 'compact'"
        :value="visibleTable"
      >
        <Column
          field="asset"
          header="Futures"
          sortable
          frozen
          :headerStyle="{ minWidth: '130px !important' }"
          :bodyStyle="{ minWidth: '130px !important' }"
        >
          <template #body="{ data }">
            <div
              class="d-flex align-items-center h-100 w-100"
              :class="{ ovPadding: !data.__isHeader }"
            >
              <span
                v-if="data.__isHeader"
                class="expand-arrow"
                @click="toggleGroup(data.__groupId)"
                style="cursor: pointer; margin: 0 5px;"
              >
                <i
                  :class="
                    `fa fa-chevron-${
                      groupExpanded[data.__groupId] ? 'down' : 'right'
                    }`
                  "
                  aria-hidden="true"
                ></i>
              </span>

              <ButtonView
                v-if="TAVisible > -1"
                class="mr-2"
                :class="{ 'ml-4': !data.__isHeader }"
                fontSize="12"
                :showBorder="false"
                @btnAbort="
                  () =>
                    onUserShowTAChart(
                      data.continuous_contract,
                      data.pk_idcommodity
                    )
                "
              />
              {{ data.__isHeader ? data.asset : data.contract_month_year }}
            </div>
          </template>
        </Column>

        <Column field="close" header="Last" sortable style="min-width: 100px">
          <template #body="{ data }">
            <div
              class="d-flex align-items-center h-100 w-100 ovHighlight ovPadding"
              :class="{
                row_up: shouldAnimateUp(data),
                row_down: shouldAnimateDown(data)
              }"
              v-if="!data.__isHeader || !groupExpanded[data.__groupId]"
            >
              <span
                :class="arrowClass(data.change)"
                v-if="data.change !== 0"
              ></span>
              <span class="ml-1"> {{ priceFormat(data.close) }}</span>
            </div>
          </template>
        </Column>

        <Column
          v-if="showFields"
          sortable
          field="change"
          header="Change"
          style="min-width: 100px"
        >
          <template #body="{ data }">
            <div
              v-if="!data.__isHeader || !groupExpanded[data.__groupId]"
              class="d-flex align-items-center h-100 w-100 ovPadding"
              :class="{
                row_up: shouldAnimateUp(data),
                row_down: shouldAnimateDown(data)
              }"
            >
              <span
                :class="{ dayUp: data.change > 0, dayDown: data.change < 0 }"
              >
                {{ changeFormat(data.change, data.close) }}
              </span>
            </div>
          </template>
        </Column>

        <Column
          field="pct_change"
          header="% Chg"
          sortable
          style="min-width: 100px"
        >
          <template #body="{ data }">
            <div
              v-if="!data.__isHeader || !groupExpanded[data.__groupId]"
              class="d-flex align-items-center h-100 w-100 ovPadding"
              :class="{
                row_up: shouldAnimateUp(data),
                row_down: shouldAnimateDown(data)
              }"
            >
              <span
                :class="{ dayUp: data.change > 0, dayDown: data.change < 0 }"
              >
                {{ percentageFormat(data.pct_change) }}
              </span>
            </div>
          </template>
        </Column>

        <Column
          v-if="showFields"
          field="pct_change_5"
          header="% 5D"
          sortable
          style="min-width: 100px"
        >
          <template #body="{ data }">
            <div
              v-if="!data.__isHeader || !groupExpanded[data.__groupId]"
              class="d-flex align-items-center h-100 w-100 ovPadding"
              :class="{
                row_up: shouldAnimateUp(data),
                row_down: shouldAnimateDown(data)
              }"
            >
              <span
                :class="{
                  dayUp: data.pct_change_5 > 0,
                  dayDown: data.pct_change_5 < 0
                }"
              >
                {{ percentageFormat(data.pct_change_5) }}
              </span>
            </div>
          </template>
        </Column>

        <Column
          v-if="showFields"
          field="pct_change_10"
          header="% 10D"
          sortable
          style="min-width: 100px"
        >
          <template #body="{ data }">
            <div
              v-if="!data.__isHeader || !groupExpanded[data.__groupId]"
              class="d-flex align-items-center h-100 w-100 ovPadding"
              :class="{
                row_up: shouldAnimateUp(data),
                row_down: shouldAnimateDown(data)
              }"
            >
              <span
                :class="{
                  dayUp: data.pct_change_10 > 0,
                  dayDown: data.pct_change_10 < 0
                }"
              >
                {{ percentageFormat(data.pct_change_10) }}
              </span>
            </div>
          </template>
        </Column>

        <Column
          v-if="showFields"
          field="pct_change_20"
          header="% 20D"
          sortable
          style="min-width: 100px"
        >
          <template #body="{ data }">
            <div
              v-if="!data.__isHeader || !groupExpanded[data.__groupId]"
              class="d-flex align-items-center h-100 w-100 ovPadding"
              :class="{
                row_up: shouldAnimateUp(data),
                row_down: shouldAnimateDown(data)
              }"
            >
              <span
                :class="{
                  dayUp: data.pct_change_20 > 0,
                  dayDown: data.pct_change_20 < 0
                }"
              >
                {{ percentageFormat(data.pct_change_20) }}
              </span>
            </div>
          </template>
        </Column>

        <Column
          v-if="showConvertedClose && showFields"
          field="converted_close"
          header="Last (USD/mt)"
          sortable
          style="min-width: 100px"
        >
          <template #body="{ data }">
            <div
              v-if="!data.__isHeader || !groupExpanded[data.__groupId]"
              class="d-flex align-items-center h-100 w-100 ovHighlight ovPadding"
              :class="{
                row_up: shouldAnimateUp(data),
                row_down: shouldAnimateDown(data)
              }"
            >
              <span
                :class="arrowClass(data.change)"
                v-if="data.change !== 0"
              ></span>
              <span class="ml-1"> {{ priceFormat(data.converted_close) }}</span>
            </div>
          </template>
        </Column>

        <Column
          v-if="showFields"
          field="open"
          header="Open"
          style="min-width: 100px"
        >
          <template #body="{ data }">
            <div
              v-if="!data.__isHeader || !groupExpanded[data.__groupId]"
              class="d-flex align-items-center h-100 w-100 ovPadding"
            >
              {{ priceFormat(data.open) }}
            </div>
          </template>
        </Column>

        <Column field="high" header="High" style="min-width: 100px">
          <template #body="{ data }">
            <div
              v-if="!data.__isHeader || !groupExpanded[data.__groupId]"
              class="d-flex align-items-center h-100 w-100 ovPadding"
            >
              {{ priceFormat(data.high) }}
            </div>
          </template>
        </Column>

        <Column field="low" header="Low" style="min-width: 100px">
          <template #body="{ data }">
            <div
              v-if="!data.__isHeader || !groupExpanded[data.__groupId]"
              class="d-flex align-items-center h-100 w-100 ovPadding"
            >
              {{ priceFormat(data.low) }}
            </div>
          </template>
        </Column>

        <Column
          v-if="showFields"
          field="prev_close"
          header="Previous"
          style="min-width: 100px"
        >
          <template #body="{ data }">
            <div
              v-if="!data.__isHeader || !groupExpanded[data.__groupId]"
              class="d-flex align-items-center h-100 w-100 ovPadding"
            >
              {{ priceFormat(data.prev_close) }}
            </div>
          </template>
        </Column>

        <Column
          v-if="showFields"
          field="volumes"
          header="Volume"
          style="min-width: 100px"
        >
          <template #body="{ data }">
            <div
              v-if="!data.__isHeader || !groupExpanded[data.__groupId]"
              class="d-flex align-items-center h-100 w-100 ovPadding"
            >
              {{ numFormat(data.volumes) }}
            </div>
          </template>
        </Column>

        <Column
          v-if="showFields"
          field="openinterest"
          header="Open Int"
          style="min-width: 100px"
        >
          <template #body="{ data }">
            <div
              v-if="!data.__isHeader || !groupExpanded[data.__groupId]"
              class="d-flex align-items-center h-100 w-100 ovPadding"
            >
              {{ numFormat(data.openinterest) }}
            </div>
          </template>
        </Column>

        <Column
          v-if="showFields"
          field="observation_date"
          header="Updated"
          style="min-width: 100px"
        >
          <template #body="{ data }">
            <div
              v-if="!data.__isHeader || !groupExpanded[data.__groupId]"
              class="d-flex align-items-center h-100 w-100 ovPadding"
            >
              {{ dateFormat(data.observation_date) }}
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script>
import ButtonView from "@/components/buttons/ButtonView.vue";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import SkeletonSingleCommodity from "@/components/overview/SkeletonSingleCommodity";
import { format, timeFormat } from "d3";

export default {
  name: "SingleCommodityTable",
  props: {
    animateUp: { required: true, default: () => [] },
    animateDown: { required: true, default: () => [] },
    colorTheme: { required: true, type: Boolean, default: true },
    commoditySpecs: { required: true, default: () => [] },
    commodityTable: { required: true, default: () => [] },
    componentNumber: { required: true, default: 1 },
    showFields: { required: true, type: Boolean, default: false },
    TAVisible: { required: false, type: Number, default: -1 }
  },
  components: {
    ButtonView,
    Column,
    DataTable,
    SkeletonSingleCommodity
  },
  data() {
    return {
      containerHeight: 200,
      groupExpanded: {}
    };
  },
  mounted() {
    this.updateHeight();
    window.addEventListener("resize", this.updateHeight);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateHeight);
  },
  computed: {
    showConvertedClose() {
      return (
        this.commodityTable &&
        this.commodityTable.some(
          item => item.conversion != null && item.conversion != 0
        )
      );
    },
    showLoading() {
      return this.commodityTable.length === 0;
    },
    processedTable() {
      const rows = [];
      let currentAsset = null;
      let groupId = 0;
      let originalIndex = 0;

      this.commodityTable.forEach(row => {
        const isNewGroup = row.asset !== currentAsset;

        if (isNewGroup) {
          groupId++;
          currentAsset = row.asset;

          if (this.groupExpanded[groupId] === undefined) {
            this.$set(this.groupExpanded, groupId, true);
          }

          rows.push({
            ...row,
            __groupId: groupId,
            __isHeader: true,
            __expanded: true,
            __originalIndex: originalIndex
          });
        } else {
          rows.push({
            ...row,
            __groupId: groupId,
            __isHeader: false,
            __originalIndex: originalIndex
          });
        }
        originalIndex++;
      });

      return rows;
    },
    visibleTable() {
      return this.processedTable.filter(row => {
        if (row.__isHeader) return true;

        return this.groupExpanded[row.__groupId];
      });
    }
  },
  watch: {},
  methods: {
    toggleGroup(groupId) {
      this.$set(this.groupExpanded, groupId, !this.groupExpanded[groupId]);
    },
    onUserShowTAChart(idx, asset) {
      console.log("idx", idx);
      console.log("asset", asset);
      const assetIndex = this.commoditySpecs.findIndex(
        obj => obj.pk_idcommodity === asset
      );
      this.$emit("eventOnUserShowTAChart", {
        assetType: 1,
        irAvailableTerms: [{ label: "...", value: "..." }],
        selectedAsset: assetIndex,
        selectedTerm: null,
        continuous_contract: +idx,
        showFields: null,
        componentNumber: this.componentNumber
      });
    },
    priceFormat(val) {
      return val === null || !isFinite(val) || val === 0
        ? "-"
        : Math.abs(val) > 10000
        ? format(",.0f")(val)
        : Math.abs(val) > 100
        ? format(",.2f")(val)
        : Math.abs(val) > 10
        ? format(",.3f")(val)
        : format(",.4f")(val);
    },
    numFormat(val) {
      return val === null || !isFinite(val) || val === 0
        ? "-"
        : format(",")(val);
    },
    changeFormat(val, price) {
      return val === null || !isFinite(val) || val === 0
        ? "-"
        : Math.abs(price) > 10000
        ? format(",.0f")(val)
        : Math.abs(price) > 100
        ? format(",.2f")(val)
        : Math.abs(price) > 10
        ? format(",.3f")(val)
        : format(",.4f")(val);
    },
    percentageFormat(val) {
      if (val == null || !isFinite(val) || val === 0) return "-";
      return format(",.2%")(val);
    },
    dateFormat(val) {
      if (!val) return "";
      const dateVal = new Date(val);
      const today = new Date();
      const sameDay = dateVal.toDateString() === today.toDateString();
      if (sameDay && dateVal.getHours() === 0 && dateVal.getMinutes() === 0) {
        return timeFormat("%d%b%y")(dateVal);
      }
      return sameDay
        ? timeFormat("%H:%M")(dateVal)
        : timeFormat("%d%b%y")(dateVal);
    },
    updateHeight() {
      const el = this.$refs.componentContainer;
      if (el) {
        this.containerHeight = el.offsetHeight - 5; // o el.clientHeight
      }
    },
    arrowClass(value) {
      if (value > 0) return "dayUp fa fa-play rotateUp";
      if (value < 0) return "dayDown fa fa-play rotateDown";
      return "";
    },
    shouldAnimateUp(data) {
      return this.animateUp.includes(data.__originalIndex);
    },
    shouldAnimateDown(data) {
      return this.animateDown.includes(data.__originalIndex);
    }
  },
  filters: {}
};
</script>
<style scoped>
.min_w {
  min-width: 60px;
}

.dayUp {
  color: var(--symmetry-h);
}
.rotateUp {
  transform: rotate(270deg) !important;
}

.dayDown {
  color: var(--red);
}
.rotateDown {
  transform: rotate(90deg) !important;
}

.row_up {
  background-color: var(--expo-alpha-3);
  transition: background-color 0.5s ease !important;
}

.row_down {
  background-color: var(--red-alpha-3);
  transition: background-color 0.5s ease !important;
}

.row_up {
  animation: pulse-up 1s ease-in-out;
}

.row_down {
  animation: pulse-down 1s ease-in-out;
}

@keyframes pulse-up {
  0%,
  100% {
    background-color: var(--expo-alpha-3);
  }

  50% {
    background-color: var(--expo-alpha-2);
  }
}

@keyframes pulse-down {
  0%,
  100% {
    background-color: var(--red-alpha-3);
  }

  50% {
    background-color: var(--red-alpha-2);
  }
}

.real_time_table {
  table-layout: fixed;
  width: 100%;
}
.real_time_table th,
.real_time_table td {
  width: auto;
  text-align: center;
}

.real_time_table td.row_up,
.real_time_table td.row_down {
  background-color: transparent !important;
}

.active .toggle__switch_g::after {
  left: 50%;
  border: 1px solid var(--expo);
  width: 50%;
  background: var(--expo);
  box-shadow: 0 0 1px var(--expo);
}
.toggle__button_g .toggle__switch_g::after,
.toggle__button_g .toggle__switch_g::before {
  content: "";
}
</style>
